import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<TscnCanvas>` renders the parsed scene in `HierarchyContext` under
 * react-three-fiber, with its resources in scope and the camera the user picks.
 * With no scene loaded, it renders an empty canvas with default lighting.
 */
import { Canvas, useThree } from '@react-three/fiber';
import { useEffect } from 'react';
import { useOptionalHierarchy } from './contexts/HierarchyContext.js';
import { useSeatedRoots } from './useLiveSceneTree.js';
import { useOptionalViewportActions } from './contexts/ViewportActionsContext.js';
import { useViewportMode } from './contexts/ViewportModeContext.js';
import { SceneResourcesProvider } from './SceneResourcesContext.js';
import { EditedRootProvider } from './contexts/InstanceOwnersContext.js';
import { NodeDispatcher } from './NodeDispatcher.js';
import { SelectionHighlight } from './components/SelectionHighlight.js';
import { HoverHighlight } from './components/HoverHighlight.js';
import { InternalTextLabel } from './internalTextLabel.js';
import { editorHelperUserData } from './editorHelpers.js';
import { FrameSelectedShortcut } from './FrameSelectedShortcut.js';
import { EditorControlsHandle, GodotEditorControls } from './GodotEditorControls.js';
import { WorldEnvironmentScope } from './environment/WorldEnvironmentScope.js';
import { EDITOR_CAMERA_FOV, editorCameraPosition } from './godotEditorCamera.js';
import { ViewportPassOrchestrator } from './contexts/ViewportPassRegistryContext.js';
import { ControlRasterLayer } from '../nodes/viewport/subviewport/ControlRasterLayer.js';
import { installShaderPatches } from './shaderPatch/installShaderPatches.js';
import { SceneShadowFitter } from './SceneShadowFitter.js';
import { SceneMipmapBias } from './materials/SceneMipmapBias.js';
import { SceneDepthPrepass } from './depthPrepass/SceneDepthPrepass.js';
import { ScreenshotBridge } from './ScreenshotBridge.js';
import { TiledUploadDriver } from './tiledUpload/TiledUploadDriver.js';
import { ActiveCameraSwitcher } from './ActiveCameraSwitcher.js';
import { CameraFit } from './CameraFit.js';
import { SceneCamerasProvider } from './sceneCameras.js';
import { CanvasServices } from './CanvasServices.js';
import styles from './TscnCanvas.module.css';
/**
 * The contents of the `<Canvas>`, exported so `@react-three/test-renderer`,
 * which cannot wrap a real `<Canvas>`, can mount it. With no `HierarchyContext`
 * provider it renders the empty default-lighting scene.
 */
export function TscnSceneContents() {
    const hierarchy = useOptionalHierarchy();
    const sceneGraph = hierarchy?.sceneGraph ?? null;
    const rootScene = sceneGraph?.scenes.get(sceneGraph.rootScene);
    const nodes = useSeatedRoots() ?? null;
    // A grid and a prompt, since an empty black canvas looks like a renderer crash.
    const isEmpty = nodes === null || nodes.length === 0;
    return (_jsx(TiledUploadDriver, { children: _jsxs(WorldEnvironmentScope, { world: "root", children: [isEmpty && _jsx(EmptySceneIndicator, {}), !isEmpty && _jsx(ContentGroundGrid, {}), nodes && rootScene && (_jsx(SceneResourcesProvider, { internalResources: rootScene.internalResources, externalResources: rootScene.externalResources, children: _jsx(EditedRootProvider, { editableInstances: rootScene.editableInstances, children: _jsx(NodeDispatcher, { nodes: nodes }) }) })), _jsx(ControlRasterLayer, { nodes: nodes ?? [], internalResources: rootScene?.internalResources ?? [], externalResources: rootScene?.externalResources ?? [] }), _jsx(ViewportPassOrchestrator, {}), _jsx(SceneShadowFitter, {}), _jsx(SceneMipmapBias, {}), _jsx(SceneDepthPrepass, {}), _jsx(SelectionHighlight, {}), _jsx(HoverHighlight, {}), _jsx(ScreenshotBridge, {})] }) }));
}
/**
 * The ground-plane grid shared by the empty-scene indicator and the content grid.
 * It carries the `tscnEmptyState` tag `frameSceneBounds` skips on itself:
 * `traverse()` visits every descendant whatever the tag of an ancestor, and the
 * 10-unit grid would otherwise frame a mesh-free scene, such as a lone Path3D.
 * It is editor chrome too, so a SubViewport pass draws none of it.
 */
function GroundGrid() {
    return _jsx("gridHelper", { args: [10, 10, 0x444444, 0x222222], userData: GROUND_GRID_USER_DATA });
}
/** One object, as R3F assigns a new `userData` object on each render. */
const GROUND_GRID_USER_DATA = { tscnEmptyState: true, ...editorHelperUserData() };
/**
 * The opt-in grid for a non-empty scene, off by default (see ViewportModeContext).
 * A child component, so a toolbar toggle re-renders this and not the node tree.
 */
function ContentGroundGrid() {
    const { showGrid } = useViewportMode();
    return showGrid ? _jsx(GroundGrid, {}) : null;
}
function EmptySceneIndicator() {
    // paint-order-safe: the empty-scene indicator, mounted where no scene and
    // so no canvas item exists.
    return (_jsxs("group", { userData: { tscnEmptyState: true }, children: [_jsx(GroundGrid, {}), _jsx(InternalTextLabel, { text: "Load a scene to begin", position: [0, 0.4, 0], fontSize: 0.35, color: "#888888" })] }));
}
/** Reads everything from context. A test mounts `<TscnSceneContents>` instead. */
export function TscnCanvas() {
    return (_jsx("div", { className: styles.root, children: _jsxs(Canvas, { camera: { position: editorCameraPosition(), fov: EDITOR_CAMERA_FOV }, shadows: "percentage", gl: { localClippingEnabled: true, antialias: false }, flat: true, onCreated: installShaderPatches, children: [_jsx(CanvasServices, {}), _jsxs(SceneCamerasProvider, { children: [_jsx(TscnSceneContents, {}), _jsx(ActiveCameraSwitcher, {})] }), _jsx(CameraFit, {}), _jsx(FrameSelectedShortcut, {}), _jsx(GodotEditorControls, {}), _jsx(EditorControlsResetBridge, {})] }) }));
}
/**
 * Hands the navigation handle to `ViewportActionsContext`, so the toolbar can call
 * `reset()` from outside the `<Canvas>`. `<GodotEditorControls>` publishes it
 * as R3F's `state.controls`.
 */
function EditorControlsResetBridge() {
    const control = useOptionalViewportActions();
    const registerResetHandler = control?.registerResetHandler;
    const published = useThree((s) => s.controls);
    const controls = published instanceof EditorControlsHandle ? published : null;
    useEffect(() => {
        if (!registerResetHandler || !controls)
            return undefined;
        return registerResetHandler(() => controls.reset());
    }, [registerResetHandler, controls]);
    return null;
}
//# sourceMappingURL=TscnCanvas.js.map